
import { useState } from "react";
import { ContentFormData, FormSubmissionState } from "../types";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useNotifications } from "@/contexts/NotificationContext";

export function useContentCreation(onContentAdded: (newItem: any) => void) {
  const [submissionState, setSubmissionState] = useState<FormSubmissionState>({
    isSubmitting: false,
    error: null
  });

  const { addNotification } = useNotifications();
  
  const [newContent, setNewContent] = useState<ContentFormData>({
    title: "",
    type: "video",
    credits: 5,
    isPublic: true,
    isPremiere: false,
    file: null,
    duration: "",
    thumbnail: null
  });

  const handleAddContent = async () => {
    if (!newContent.title) {
      toast({
        title: "Σφάλμα",
        description: "Παρακαλώ συμπληρώστε τον τίτλο",
        variant: "destructive"
      });
      return;
    }
    
    try {
      setSubmissionState({ isSubmitting: true, error: null });
      
      // Upload files to storage if needed in a real application
      // For demo purposes, we'll just use the given URLs
      
      // Insert into Supabase
      const { data, error } = await supabase
        .from('videos')
        .insert({
          title: newContent.title,
          duration: newContent.duration || "00:00",
          credits: newContent.credits,
          is_premiere: newContent.isPremiere,
          url: newContent.file ? URL.createObjectURL(newContent.file) : null,
          category: "general",
          thumbnail_url: newContent.thumbnail ? URL.createObjectURL(newContent.thumbnail) : "/placeholder.svg"
        })
        .select();
      
      if (error) throw error;
      
      if (data && data[0]) {
        // Add to local content list
        const newItem = {
          id: data[0].id,
          title: data[0].title,
          type: "video",
          credits: data[0].credits,
          isPublic: true,
          isPremiere: data[0].is_premiere,
          fileUrl: data[0].url,
          duration: data[0].duration,
          thumbnailUrl: data[0].thumbnail_url
        };
        
        // Call the callback to add to parent state
        onContentAdded(newItem);
        
        // Notify users if it's a premiere
        if (newContent.isPremiere) {
          // In a real application, this would send notifications to users
          addNotification(
            "Νέο Περιεχόμενο",
            `Προστέθηκε νέο περιεχόμενο: ${newContent.title}`,
            true
          );
          
          toast({
            title: "Ειδοποίηση προς χρήστες",
            description: `Ενημερώθηκαν οι χρήστες για το νέο περιεχόμενο "${newContent.title}"`
          });
        }
        
        toast({
          title: "Επιτυχία",
          description: "Το περιεχόμενο προστέθηκε επιτυχώς"
        });
        
        // Reset form
        setNewContent({
          title: "",
          type: "video",
          credits: 5,
          isPublic: true,
          isPremiere: false,
          file: null,
          duration: "",
          thumbnail: null
        });
      }
    } catch (error: any) {
      console.error("Error adding new content:", error);
      setSubmissionState({
        isSubmitting: false,
        error: error.message || "Παρουσιάστηκε πρόβλημα κατά την προσθήκη του περιεχομένου"
      });
      
      toast({
        title: "Σφάλμα",
        description: "Παρουσιάστηκε πρόβλημα κατά την προσθήκη του περιεχομένου",
        variant: "destructive"
      });
      return;
    } finally {
      setSubmissionState({ isSubmitting: false, error: null });
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setNewContent({ ...newContent, file: e.target.files[0] });
    }
  };
  
  const handleThumbnailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setNewContent({ ...newContent, thumbnail: e.target.files[0] });
    }
  };

  const handleContentChange = (updatedContent: ContentFormData) => {
    setNewContent(updatedContent);
  };

  return {
    newContent,
    submissionState,
    handleAddContent,
    handleFileChange,
    handleThumbnailChange,
    handleContentChange
  };
}
